import React, {
  useRef,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useState,
} from "react";
import {
  GoogleMap,
  useJsApiLoader,
  DrawingManager,
} from "@react-google-maps/api";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";

const containerStyle = {
  width: "100%",
  height: "100%",
};

const center = {
  lat: 37.7749,
  lng: -122.4194,
};

const REACT_APP_MAP_TOKEN = process.env.REACT_APP_MAP_TOKEN;
const libraries = ["places", "drawing", "marker", "core"];

const MapDisplay = forwardRef(
  (
    {
      mode,
      shapeType,
      shapeName,
      layoutData,
      setLayoutData,
      onShapeDrawn,
      onShapeEditInitiated,
      onShapeEditConfirmed,
    },
    ref
  ) => {
    const drawingManagerRef = useRef(null);
    const mapRef = useRef(null);
    const modeRef = useRef(mode);
    const [shapes, setShapes] = useState([]);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedShape, setSelectedShape] = useState(null);

    useImperativeHandle(ref, () => ({
      cancelDrawMode: () => {
        if (drawingManagerRef.current) {
          drawingManagerRef.current.setDrawingMode(null);
        }
      },
    }));

    const { isLoaded, loadError } = useJsApiLoader({
      googleMapsApiKey: REACT_APP_MAP_TOKEN,
      libraries,
    });

    useEffect(() => {
      modeRef.current = mode;
    }, [mode]);

    const getDrawingMode = () => {
      if (mode !== "draw") return null;
      return shapeType === "zone"
        ? window.google.maps.drawing.OverlayType.CIRCLE
        : window.google.maps.drawing.OverlayType.POLYGON;
    };

    const handleOverlayComplete = (event) => {
      const overlay = event.overlay;
      const type = shapeType;
      const id = Date.now();

      let shapeData = null;
      if (event.type === "circle") {
        shapeData = {
          id,
          type,
          name: shapeName,
          center: {
            lat: overlay.getCenter().lat(),
            lng: overlay.getCenter().lng(),
          },
          radius: overlay.getRadius(),
        };
      } else if (event.type === "polygon") {
        const path = overlay.getPath().getArray();
        shapeData = {
          id,
          type,
          name: shapeName,
          paths: path.map((p) => ({ lat: p.lat(), lng: p.lng() })),
        };
      }

      overlay.id = id;
      overlay.type = type;
      overlay.name = shapeName;
      overlay.setEditable(false);

      overlay.addListener("click", () => {
        console.log("Shape clicked", overlay);
        console.log("Current mode: ", modeRef.current);
        if (modeRef.current === "edit") {
          shapes.forEach((shape) => shape.setEditable(false));
          overlay.setEditable(true);

          if (onShapeEditInitiated) {
            const shapeInfo =
              overlay.type === "zone"
                ? {
                    id: overlay.id,
                    name: overlay.name,
                    center: {
                      lat: overlay.getCenter().lat(),
                      lng: overlay.getCenter().lng(),
                    },
                    radius: overlay.getRadius(),
                    type: overlay.type,
                  }
                : {
                    id: overlay.id,
                    name: overlay.name,
                    paths: overlay
                      .getPath()
                      .getArray()
                      .map((p) => ({ lat: p.lat(), lng: p.lng() })),
                    type: overlay.type,
                  };

            onShapeEditInitiated(shapeInfo);
          }
        } else if (modeRef.current === "delete") {
          setSelectedShape(overlay);
          setDeleteModalOpen(true);
        }
      });

      setShapes((prev) => [...prev, overlay]);

      if (shapeData) {
        onShapeDrawn(shapeData);
      }
    };

    const handleDeleteConfirmed = () => {
      if (selectedShape) {
        selectedShape.setMap(null);
        setShapes((prev) => prev.filter((shape) => shape !== selectedShape));
        setLayoutData((prev) => {
          const newLayout = JSON.parse(JSON.stringify(prev));
          const type = selectedShape.type;
          newLayout[type + "s"] = newLayout[type + "s"].filter(
            (s) => s.id !== selectedShape.id
          );
          return newLayout;
        });
      }
      setDeleteModalOpen(false);
      setSelectedShape(null);
    };

    const handleDeleteCancelled = () => {
      setDeleteModalOpen(false);
      setSelectedShape(null);
    };

    useEffect(() => {
      if (!onShapeEditConfirmed) return;

      const handler = ({ id, name }) => {
        setLayoutData((prev) => {
          const updated = JSON.parse(JSON.stringify(prev));
          const keys = Object.keys(updated);

          for (let key of keys) {
            const index = updated[key].findIndex((s) => s.id === id);
            if (index !== -1) {
              updated[key][index].name = name;
              break;
            }
          }

          return updated;
        });
      };

      onShapeEditConfirmed(handler);
    }, [onShapeEditConfirmed]);

    useEffect(() => {
      if (mode !== "edit") {
        shapes.forEach((shape) => shape.setEditable(false));
      }
    }, [mode]);

    if (loadError) return <div>Error loading map</div>;
    if (!isLoaded) return <div>Loading map...</div>;

    return (
      <>
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={center}
          zoom={15}
          onLoad={(map) => {
            mapRef.current = map;
          }}
        >
          {mode === "draw" && (
            <DrawingManager
              onLoad={(dm) => {
                drawingManagerRef.current = dm;
              }}
              onOverlayComplete={handleOverlayComplete}
              options={{
                drawingControl: false,
                drawingMode: getDrawingMode(),
                polygonOptions: {
                  // fillColor: "#FF0000",
                  //if shapeType is geofence, use yellow, else use pink
                  fillColor: shapeType === "geofence" ? "#FFFF00" : "#FF00FF",
                  fillOpacity: 0.5,
                  strokeWeight: 2,
                  clickable: true,
                  editable: false,
                  zIndex: 1,
                },
                circleOptions: {
                  // fillColor: "#FF0000",
                  fillColor: "blue",
                  fillOpacity: 0.5,
                  strokeWeight: 2,
                  clickable: true,
                  editable: false,
                  zIndex: 1,
                },
              }}
            />
          )}
        </GoogleMap>

        <Dialog open={deleteModalOpen} onClose={handleDeleteCancelled}>
          <DialogTitle>Delete Shape?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete this shape? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteCancelled}>Cancel</Button>
            <Button onClick={handleDeleteConfirmed} color="error">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }
);

export default MapDisplay;
